import React, { useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
    ClassicEditor,
    Bold,
    Italic,
    Paragraph,
    Heading,
    List,
    Link,
    LinkEditing,
    LinkUI,
    Table,
    TableToolbar,
    SourceEditing,
    FontColor,
    FontSize,
    Alignment,
    Indent,
    IndentBlock,
    BlockQuote,
    Highlight
} from "ckeditor5";
import 'ckeditor5/ckeditor5.css';

const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
};

const MyEditor = ({ content, onChange }) => {
    const editorContainerRef = useRef(null);

    useEffect(() => {
        const element = editorContainerRef.current;

        if (element) {
            const handleResize = debounce(() => {
                // console.log('Element resized:', element.getBoundingClientRect());
            }, 100); // Adjust the debounce delay as needed

            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(element);

            return () => {
                resizeObserver.unobserve(element);
            };
        }
    }, []);

    return (
        <div className="ctm-editor" ref={editorContainerRef}>
            <CKEditor
                editor={ClassicEditor}
                config={{
                    licenseKey: "GPL",
                    plugins: [
                        Bold,
                        Italic,
                        Paragraph,
                        Heading,
                        List,
                        Link,
                        LinkEditing,
                        LinkUI,
                        Table,
                        TableToolbar,
                        SourceEditing,
                        FontColor,
                        FontSize,
                        Alignment,
                        Indent,
                        IndentBlock,
                        BlockQuote,
                        Highlight
                    ],
                    toolbar: {
                        items: [
                            'heading',
                            '|',
                            'bold',
                            'italic',
                            'link',
                            '|',
                            'bulletedList',
                            'numberedList',
                            '|',
                            'outdent',
                            'indent',
                            '|',
                            'blockQuote',
                            'insertTable',
                            '|',
                            'fontColor',
                            'fontSize',
                            '|',
                            'sourceEditing'
                        ],
                        shouldNotGroupWhenFull: true
                    },
                    heading: {
                        options: [
                            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
                        ]
                    },
                    fontSize: {
                        options: [
                            'tiny',
                            'small',
                            'default',
                            'big',
                            'huge'
                        ]
                    },
                    fontColor: {
                        colors: [
                            { color: '#000000', label: 'Black' },
                            { color: '#4D4D4D', label: 'Dark Gray' },
                            { color: '#999999', label: 'Gray' },
                            { color: '#E6E6E6', label: 'Light Gray' },
                            { color: '#FFFFFF', label: 'White' },
                            { color: '#FF0000', label: 'Red' },
                            { color: '#00FF00', label: 'Green' },
                            { color: '#0000FF', label: 'Blue' }
                        ]
                    },
                    table: {
                        contentToolbar: [
                            'tableColumn',
                            'tableRow',
                            'mergeTableCells'
                        ]
                    },
                    link: {
                        addTargetToExternalLinks: true,
                        defaultProtocol: 'https://'
                    }
                }}
                data={content}
                onChange={(event, editor) => {
                    const data = editor.getData();
                    onChange(data);
                }}
            />
        </div>
    );
};

export default MyEditor;
